<template>
  <div :id="ssrData?.elementId || `bg-fallback-${instanceId}`" class="dynamic-background-image">
    <slot />
  </div>
</template>

<script setup lang="ts">
import { breakpoints } from '~~/configs/breakpoints.config';
import type { IBreakpoint, IFile, IImageRendition } from '~~/types/local_types';

interface IProps {
  renditions: IImageRendition[];
  image: IFile;
  backgroundSize?: string;
  backgroundRepeat?: string;
  backgroundPosition?: string;
  backgroundColor?: string;
}

const props = withDefaults(defineProps<IProps>(), {
  backgroundSize: 'cover',
  backgroundRepeat: undefined,
  backgroundPosition: undefined,
  backgroundColor: undefined,
});

const { directusAssetsUrl } = useRuntimeConfig().public;

// 1. GENERATE IDENTIFIERS
// This ID is used for the DOM, but NOT for the transport key.
const instanceId = useId();

// This key is based purely on data.
// If the data is the same, the key is the same, and the hydration matches.
const componentFingerprint = computed(() => {
  const rendHash =
    props.renditions?.map((r) => `${r.breakpoint}${r.size}${r.aspectRatio}`).join('-') || 'no-rend';
  const imgId = props.image?.id || 'no-img';
  return `bg-${imgId}-${rendHash}`;
});

// 2. CSS GENERATOR
const generateStyleString = (elementId: string) => {
  let styleString = `#${elementId}::before {
    position: absolute;
    content: '';
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-size: ${props.backgroundSize};
  `;

  if (props.backgroundRepeat) styleString += `background-repeat: ${props.backgroundRepeat};`;
  if (props.backgroundPosition) styleString += `background-position: ${props.backgroundPosition};`;
  if (props.backgroundColor) styleString += `background-color: ${props.backgroundColor};`;

  for (let i = 0; i < breakpoints.length; i++) {
    const breakpoint = breakpoints[i] as IBreakpoint;
    const rendition = props.renditions.find((r) => r.breakpoint === breakpoint.name);

    const iso = props.image?.modifiedOn;

    let modifiedOn = '';
    if (iso) {
      const date = new Date(iso);
      modifiedOn =
        date.getFullYear() +
        '-' +
        String(date.getMonth() + 1).padStart(2, '0') +
        '-' +
        String(date.getDate()).padStart(2, '0') +
        '-' +
        String(date.getHours()).padStart(2, '0') +
        '-' +
        String(date.getMinutes()).padStart(2, '0');
    }

    if (rendition) {
      const basePath = props.image?.id ? `${directusAssetsUrl}${props.image.id}` : '';
      const [aw, ah] = rendition.aspectRatio.split(/[x:]/);
      const size = rendition.size;
      const width = Math.round(size);
      const height = Math.round((size / parseInt(aw || '16')) * parseInt(ah || '9'));

      if (breakpoint.min === 0) {
        styleString += `
          background-image: url('${basePath}.jpg?width=${width}&height=${height}&fit=cover${modifiedOn ? `&v=${modifiedOn}` : ''}');
          background-image: image-set(
            url('${basePath}.jpg?width=${width}&height=${height}&fit=cover${modifiedOn ? `&v=${modifiedOn}` : ''}') 1x,
            url('${basePath}.webp?width=${Math.floor(width * 1.5)}&height=${Math.floor(height * 1.5)}&fit=cover${modifiedOn ? `&v=${modifiedOn}` : ''}') 1.5x
          );`;
      } else {
        styleString += `
        @media (min-width: ${breakpoint.min}px) {
          background-image: url('${basePath}.jpg?width=${width}&height=${height}&fit=cover${modifiedOn ? `&v=${modifiedOn}` : ''}');
          background-image: image-set(
            url('${basePath}.jpg?width=${width}&height=${height}&fit=cover${modifiedOn ? `&v=${modifiedOn}` : ''}') 1x,
            url('${basePath}.webp?width=${Math.floor(width * 1.5)}&height=${Math.floor(height * 1.5)}&fit=cover${modifiedOn ? `&v=${modifiedOn}` : ''}') 1.5x
          );
        }
        `;
      }
    }
  }

  styleString += '}';
  return styleString;
};

// 3. DATA TRANSPORT
// CRITICAL: The key is just the fingerprint. The VALUE inside contains the drifting ID.
const { data: ssrData } = await useAsyncData(
  `transport-${componentFingerprint.value}`,
  () => {
    // This elementId is generated on the server using the server's useId() counter.
    const elementId = `dynamic-bg-${instanceId}`;
    const css = generateStyleString(elementId);
    return {
      elementId: String(elementId),
      css: String(css),
    };
  },
  { server: true, immediate: true },
);

// 4. INJECT CSS
useHead({
  style: [
    {
      // Use the server-provided ID to target the styles
      id: `style-${ssrData.value?.elementId || instanceId}`,
      innerHTML: ssrData.value?.css || '',
    },
  ],
});
</script>

<style scoped lang="postcss"></style>
